import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthContext } from '@/hooks/use-auth-context'
import { getBackendAuthToken } from '@/lib/backend-auth-token'
import {
  loadStoredGenerations,
  type StoredGeneration,
  type ToolFailure,
} from '@/lib/tool-generations'

/** Failure history survives reload; a late initial read cannot erase this mount's failure. */
export function useFailedToolRuns(slug: string) {
  const { isSignedIn, user, getToken } = useAuthContext()
  const [history, setHistory] = useState<{
    owner: string | undefined
    slug: string
    runs: StoredGeneration[]
  }>({ owner: user?.id, slug, runs: [] })
  const localIds = useRef(new Set<string>())
  // A focus can start a load while an earlier one is in flight; only the
  // newest may apply, or a slower stale read erases a fresher failure.
  const latestLoad = useRef(0)
  const owner = user?.id
  useEffect(() => {
    let cancelled = false
    localIds.current.clear()
    if (!isSignedIn) return
    const load = async () => {
      const seq = ++latestLoad.current
      const token = await getBackendAuthToken(getToken)
      if (!token || cancelled) return
      const stored = await loadStoredGenerations(token, slug, 24, true)
      if (cancelled || seq !== latestLoad.current) return
      const failures = stored.filter(run => run.status === 'FAILED')
      setHistory(previous => ({
        owner,
        slug,
        runs: [
          ...(previous.owner === owner && previous.slug === slug
            ? previous.runs.filter(
                run =>
                  localIds.current.has(run.requestId) &&
                  !failures.some(row => row.requestId === run.requestId),
              )
            : []),
          ...failures,
        ],
      }))
    }
    void load()
    window.addEventListener('focus', load)
    return () => {
      cancelled = true
      window.removeEventListener('focus', load)
    }
  }, [slug, owner, isSignedIn, getToken])
  const rememberFailure = useCallback(
    (
      requestId: string,
      prompt: string,
      deliveryMode: string | null | undefined,
      failure?: ToolFailure | null,
    ) => {
      localIds.current.add(requestId)
      setHistory(previous => ({
        owner,
        slug,
        runs: [
          {
            id: requestId,
            requestId,
            toolSlug: slug,
            outputKind: '',
            prompt,
            url: '',
            createdAt: new Date().toISOString(),
            status: 'FAILED',
            failure,
            deliveryMode: deliveryMode === 'preview' ? 'preview' : 'full',
          },
          ...(previous.owner === owner && previous.slug === slug
            ? previous.runs.filter(run => run.requestId !== requestId)
            : []),
        ],
      }))
      window.dispatchEvent(new Event('fal-tool-history-changed'))
    },
    [slug, owner],
  )
  return {
    failedRuns:
      isSignedIn && history.owner === owner && history.slug === slug
        ? history.runs
        : [],
    rememberFailure,
  }
}
